import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Website} from "../../../website";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {CloudflareService} from "../../services/cloudflare.service";
import {UserSubscription, UserSubscriptionCategory} from '../../../../../user/user-subscription';
import features from './cdn-pending-features.json';
import steps from './steps.json';
import {DbModalService} from "../../../../../modules/modal/modal";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {filter, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {DisableCloudflareEnterpriseComponent} from "../dialogs/disable-cloudflare-enterprise/disable-cloudflare-enterprise.component";
import {EditOriginalIpComponent} from "../dialogs/edit-original-ip/edit-original-ip.component";

@Component({
    selector: 'db-cdn-pending',
    templateUrl: './cdn-pending.component.html',
    styleUrls: ['./cdn-pending.component.scss'],
    standalone: false
})
export class CdnPendingComponent implements OnInit {

  @Input() currentSubscription: UserSubscription;
  @Input() server: string;
  public website: Website;
  public UserSubscriptionCategory: any = UserSubscriptionCategory;
  public features: any = features;
  public loading: boolean;
  public recheck: boolean;
  public hasCloudflareConflict: boolean;
  public recheckRequest: boolean;
  public initialLoading: boolean = true;
  public step: number = 1;
  public title: string;
  public buttonText: string;
  public originalIp: string;
  public cancelDesc: string;
  public hostname: string;
  public cdnIsLive: boolean;
  public cdnNotLiveMessage: string;
  public cdnPendingMessage: string;
  public cloudflareConflict: string;
  public dnsRecordsForCopy: any[] = [];
  public records: any[] = [];
  public isCopiedHostNames: any = [];
  public isCopiedCodes: any = [];
  public steps: any;
  public hasDcvRecord: boolean = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _dbModalService: DbModalService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _cloudflareService: CloudflareService,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) { }

  ngOnInit() {
    this.hasCloudflareConflict = this.server === 'cloudflare';
    this.website = this._route.snapshot.parent.parent.data['website'];
    this.steps = steps;
    this.cdnNotLiveMessage = "<b>Domain ownership verification failed.</b> DNS records usually take some time before they are resolved.<br/>Make sure there are no mistakes in TXT records and check again in a few minutes.";
    this.cdnPendingMessage = "<b>Domain ownership verification is in progress.</b> Please check the status again later.";
    this.cloudflareConflict = "<b>Domain ownership verification failed.</b> The hostname is already using Cloudflare. Disable proxy for this domain to pass verification. You can do it from the DNS management section of your Cloudflare account.";
    this._getDnsData();

  }


  private _getDnsData(): void {
    this._store.select(fromStore.getCloudflareData(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$),
        filter((data) => !TypeUtils.isEmpty(data)),
      )
      .subscribe(data => {
        this.initialLoading = false;
        this._resetData();
        this.hasDcvRecord = !!data.hasDcvRecord;
        this.originalIp = data.originalRecord;
        this.hostname = data.hostname;
        data && data.ownershipVerificationRecords && this.dnsRecordsForCopy.push(data.ownershipVerificationRecords);
        data && data.dcvVerificationRecords && !this.hasDcvRecord && (this.dnsRecordsForCopy = [...this.dnsRecordsForCopy, ...data.dcvVerificationRecords]);
        this.records = this.dnsRecordsForCopy;
        this.title = TextUtils.getText('text_verifyDomainStep1Title');
        this.buttonText = TextUtils.getText('text_verifyDomain');
        this.cdnIsLive = data && data.txtStatus === 'active' && data.cfStatus === 'active';
        this.recheckRequest && !this.cdnIsLive && (this.recheck = true);
        this.recheckRequest = false;
        this.hasCloudflareConflict = this.server === 'cloudflare';
        this.cancelDesc = 'If you’re having difficulties pointing your domain to Cloudflare, contact our support team for quick assistance.';
        if (!this.hasCloudflareConflict && data.verificationErrors && data.verificationErrors.length) {
          data.verificationErrors.map((error) => {
            this.hasCloudflareConflict = error.indexOf('The hostname is using Cloudflare and cannot be activated with an TXT or HTTP validation token') > -1;
          });
        }
        if (data && data.txtStatus !== 'pending') {
          this.step = 2;
          this.records = data && data.cfRecords;
          this.title = TextUtils.getText('text_verifyDomainStep2Title');
          this.buttonText = TextUtils.getText('text_proceed');
          this.cdnNotLiveMessage = "<b>Domain isn’t pointed yet.</b> DNS records can take up to 24 hours before they are resolved. Make sure<br/>there are no mistakes in A records and check again later.";
          this.cancelDesc = 'If you’re having difficulties pointing your domain to Cloudflare, contact our support team for quick assistance. <p class="mt15">If you wish to cancel the process, make sure to reverse the DNS records in your domain registrar to avoid any issues.</p>';
        }
        if (this.records && this.records.length > 0) {
          this.features[this.step] = this.features[this.step].reduce((newEntities, feature) => {
            const title = feature && feature.title && feature.title.indexOf('records') < 0 && feature.title.replaceAll('record', 'records') || feature.title;
            const withLabel = feature && feature.withLabel;
            const newObj = {
              "step": feature.step,
              "title": title,
              "withLabel": withLabel
            };
            newEntities.push(newObj);
            return newEntities;
          }, []);
        }
        else {
          this.buttonText = 'check status';
        }
        this.loading = false;
      });
  }

  public copiedHostName(index: number) {
    this.isCopiedHostNames[index] = true;
  }

  public copiedNameServer(index: number) {
    this.isCopiedCodes[index] = true;
  }

  public troubleShootVerifications(): void {
    this.loading = true;
    this.recheckRequest = true;
    this.recheck = false;
    this._store.dispatch(new fromStore.LoadCloudflareData(this.website?.id));
  }

  private _showModal(_modal, params) {
    const modal = this._dbModalService.open(_modal);
    modal.componentInstance = {...params};
  }

  public cancelProcess(): void {
    const params = {
      websiteId: this.website?.id,
      title: 'You’re about to cancel the setup process',
      desc: this.cancelDesc,
      widthConfirmation: this.step === 2,
      isActive: false,
      negativeButtonText: 'Cancel Process',
      positiveButtonText: 'Contact Us',
      sizes: {height: this.step === 1 ? 233 : 332 , width: 700},
      forCancel: true
    };
    this._showModal(DisableCloudflareEnterpriseComponent, params);
  }

  public editOriginalIp(): void {
    const params = {
      websiteId: this.website?.id,
      originalIp: this.originalIp,
      sizes: {height: 314 , width: 700}
    };
    this._showModal(EditOriginalIpComponent, params);
  }

  private _resetData(): void {
    this.dnsRecordsForCopy = [];
    this.records = [];
    this.isCopiedHostNames = [];
    this.isCopiedCodes = [];
  }
}
